/**
 * HealingArcSession — the guided writing session for healing arcs.
 *
 * Item #4: Letters to Others full arc session build.
 * Letters to Self uses the same infrastructure (Item #7, ~6 weeks later).
 *
 * Flow per session:
 *   consent    → S1 of Letters to Others only: explicit "not saved, not stored" gate
 *   writing    → multi-turn facilitated conversation; "letter" = user turns concatenated
 *   closing    → user clicks "I'm ready to close"; Claude gives somatic close
 *   burn       → BurnRitual (S1, S2 of Letters to Others; S1, S2 of Letters to Self)
 *   burn_choice → S3 of Letters to Others: user chooses save or burn
 *   saved      → S3 of Letters to Self: always saved, shown as save confirmation
 *   complete   → arc.completionLine + navigation options
 *
 * Facilitate calls use phase 1 + arc-specific seriesContext — no Edge Function
 * changes needed. The seriesContext fully overrides the standard ground behavior.
 *
 * Crisis: if facilitate returns { crisis: true }, a sticky resource banner
 * appears. The session continues — the user decides whether to stay or go.
 *
 * Arc progress update on completion:
 *   session_N_completed_at = now()
 *   current_session → N+1
 *   If N === 3: status = 'completed', completed_at = now()
 */

import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import { callClaude } from '../lib/claude'
import { getArc } from '../data/arcs'
import { localizeArc } from '../data/arcs.pl'
import { useTranslation } from 'react-i18next'
import type { ArcId } from '../data/arcs'
import BurnRitual from '../components/BurnRitual'
import type { SessionMessage } from '../types/session'

// ── Arc-specific facilitation context ─────────────────────────────────────────
// These replace the standard ground opener and keep Claude in witness-only mode.
// The instruction is injected as seriesContext — see facilitate/index.ts.

function buildArcSeriesContext(arcId: ArcId, sessionNum: number): string {
  const ctxMap: Record<string, Record<number, string>> = {
    letters_to_others: {
      1: `ARC SESSION — Letters to Others, Session 1: Truth Untold.
You are facilitating a ritual letter-writing session. The person is writing a letter to someone who hurt them. These are the words that were never said.
OPENING (only when the message is "[Begin arc session]"):
Open with exactly: "Take a breath. This space is yours. These words don't have to be perfect — they just have to be true. What do you need to say?"
DURING:
After each message, mirror what they shared in 1–2 sentences using their exact language where possible. Then ask one of: "What else needs to be said?" / "What haven't you let yourself say yet?" / "Is there anything underneath that?"
Hold space only. Never analyze, reframe, or find meaning in what happened. Never mention forgiveness, closure, or silver linings.
CLOSING (when user says they are ready to close or done):
Say one sentence of somatic grounding followed by one sentence witnessing what they brought. Example form (do not copy): "Take a slow breath. You just said what needed saying." Two sentences only. Nothing more.`,

      2: `ARC SESSION — Letters to Others, Session 2: The Deeper Layer.
The person has returned. They've had at least 72 hours since Session 1. They are going deeper — into grief, loss, and longing.
OPENING (only when the message is "[Begin arc session]"):
Open with: "You came back. Take a breath. Underneath what happened — what did you lose? What did you long for that you never got?"
DURING:
Hold the grief. Mirror what they bring with full specificity. Do not rush toward resolution. Never reframe. Ask: "What else did you lose?" or "What did the younger version of you need from this person?"
CLOSING (when user says they are ready to close):
Witness the grief in one sentence. Offer one somatic close. Nothing more.`,

      3: `ARC SESSION — Letters to Others, Session 3: Release.
This is the final letter. The framing is NOT forgiveness — it is reclamation.
Never use the word "forgiveness." This is about taking back what was always theirs.
OPENING (only when the message is "[Begin arc session]"):
Open with: "This is the final letter. Not to excuse them. To reclaim what is yours. What do you want to take back?"
DURING:
Reflect what they name as theirs to reclaim. Ask: "What else do you want to reclaim?" / "What belongs to you that they never had the right to take?"
CLOSING (when user says they are ready to close):
Reflect in one sentence what was reclaimed. Do not say the arc is complete or finished — the user leads that meaning.`,
    },

    letters_to_self: {
      1: `ARC SESSION — Letters to Self, Session 1: Voice of Pain.
The person is writing to themselves — to the part of them that was left behind or abandoned.
OPENING (only when the message is "[Begin arc session]"):
Open with: "This is a letter to yourself. Let the part of you that has been waiting to speak — speak. Don't edit it. Don't soften it. What needs to be said?"
DURING:
Mirror what they bring with full specificity. Ask: "What else does that part of you need to say?" Never reframe or suggest healing. This session is for the pain to be fully witnessed first.
CLOSING (when user says they are ready to close):
Witness what was spoken in one sentence. Offer one somatic close.`,

      2: `ARC SESSION — Letters to Self, Session 2: Voice of Understanding.
The person writes back — not to fix anything, but to begin to understand.
OPENING (only when the message is "[Begin arc session]"):
Open with: "This time, you write back. Not to fix it — just to begin to understand it. What do you see now that you couldn't see then?"
DURING:
Hold the tentative, emerging understanding. Don't rush it. Ask: "What else do you understand?" / "What was that younger part of you actually trying to do?"
CLOSING (when user says they are ready to close):
Witness the understanding that emerged in one sentence. One somatic close.`,

      3: `ARC SESSION — Letters to Self, Session 3: The Vow.
The person writes a promise to stay — not to be perfect, but to not abandon themselves again.
OPENING (only when the message is "[Begin arc session]"):
Open with: "This is the last letter. A promise to stay. Not to be perfect — just to stop leaving. What do you want to promise yourself?"
DURING:
Hold the weight of the vow. Ask: "What does staying look like for you?" / "What would it mean to really mean this?"
CLOSING (when user says they are ready to close):
Honour the vow in one sentence. Note (do not skip this): "This isn't finished. It's begun." Then one somatic close.`,
    },
  }

  return ctxMap[arcId]?.[sessionNum] ?? ''
}

// ── Crisis resources banner ────────────────────────────────────────────────────

function CrisisBanner({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation()
  // Helplines come from the locale — this banner previously listed the US
  // numbers only, which are no use to a Polish user in crisis.
  const lines = t('crisis.resources').split('\n').filter(Boolean)
  return (
    <div
      className="mx-6 mb-4 rounded-xl px-4 py-3 flex items-start gap-3"
      style={{
        background: 'rgba(220,38,38,0.12)',
        border: '1.5px solid rgba(220,38,38,0.3)',
      }}
    >
      <div className="flex-1">
        <p className="text-sm font-medium mb-1" style={{ color: '#FCA5A5' }}>
          {t('crisis.bannerHeading')}
        </p>
        <p className="text-xs leading-relaxed" style={{ color: 'rgba(252,165,165,0.8)' }}>
          {t('crisis.bannerBody')}
        </p>
        <div className="text-xs mt-2" style={{ color: 'rgba(252,165,165,0.6)' }}>
          {lines.map((line, i) => <p key={i} style={{ margin: 0 }}>{line}</p>)}
        </div>
      </div>
      <button
        onClick={onDismiss}
        style={{ background: 'none', border: 'none', color: 'rgba(252,165,165,0.5)', cursor: 'pointer', padding: 0, flexShrink: 0 }}
        aria-label={t('crisis.dismiss')}
      >
        ✕
      </button>
    </div>
  )
}

// ── Message bubble ─────────────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: SessionMessage }) {
  const isUser = msg.role === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className="max-w-[85%] rounded-2xl px-4 py-3"
        style={
          isUser
            ? { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }
            : { background: 'transparent' }
        }
      >
        <p
          className="text-sm leading-relaxed"
          style={{
            color: isUser ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.65)',
            fontFamily: isUser ? 'inherit' : 'Georgia, serif',
            fontStyle: isUser ? 'normal' : 'italic',
            lineHeight: 1.75,
            whiteSpace: 'pre-wrap',
          }}
        >
          {msg.content}
        </p>
      </div>
    </div>
  )
}

// ── Typing indicator ───────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex justify-start mb-4">
      <div className="flex items-center gap-1.5 px-4 py-3">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: 'rgba(255,255,255,0.3)',
              animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

interface HealingArcSessionProps {
  user: User
}

type Stage =
  | 'loading'
  | 'consent'
  | 'writing'
  | 'closing'
  | 'burn'
  | 'burn_choice'
  | 'saved'
  | 'complete'

export default function HealingArcSession({ user }: HealingArcSessionProps) {
  const { arcId, sessionNum: sessionNumStr } = useParams<{ arcId: string; sessionNum: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const sessionNum = parseInt(sessionNumStr ?? '1', 10) as 1 | 2 | 3

  // localizeArc preserves burnRequired and alwaysSaved, so the burn/save
  // branching below reads the same flags in either language.
  const rawArc = getArc(arcId as ArcId)
  const arc = rawArc ? localizeArc(rawArc, i18n.language) : undefined
  const sessionMeta = arc?.sessions[sessionNum - 1]

  const [stage, setStage] = useState<Stage>('loading')
  const [firstName, setFirstName] = useState('')
  const [messages, setMessages] = useState<SessionMessage[]>([])
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(false)
  const [crisis, setCrisis] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [arcProgressId, setArcProgressId] = useState<string | null>(null)
  const [savingCompletion, setSavingCompletion] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Load profile + arc progress row on mount
  useEffect(() => {
    if (!arc) return

    Promise.all([
      Promise.resolve(
        db().from('profiles').select('display_name').eq('id', user.id).single()
      ),
      Promise.resolve(
        db()
          .from('healing_arc_progress')
          .select('id')
          .eq('user_id', user.id)
          .eq('arc_id', arc.id)
          .eq('status', 'in_progress')
          .single()
      ),
    ]).then(([profileRes, progressRes]) => {
      setFirstName((profileRes.data as { display_name: string | null } | null)?.display_name ?? '')
      setArcProgressId((progressRes.data as { id: string } | null)?.id ?? null)

      // S1 of Letters to Others gets a consent gate
      if (arc.id === 'letters_to_others' && sessionNum === 1) {
        setStage('consent')
      } else {
        beginSession()
      }
    }).catch(() => {
      beginSession()
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, loading])

  async function beginSession() {
    setStage('writing')
    setLoading(true)
    const seriesCtx = buildArcSeriesContext(arcId as ArcId, sessionNum)
    const result = await callClaude({
      phase: 1,
      emotion: 'grief',
      intensity: 7,
      messages: [{ role: 'user', content: '[Begin arc session]' }],
      firstName,
      seriesContext: seriesCtx,
    })
    setLoading(false)
    if ('error' in result) {
      setError(result.error)
      return
    }
    if (result.crisis) setCrisis(true)
    setMessages([{ role: 'assistant', content: result.response }])
  }

  async function sendMessage() {
    if (!draft.trim() || loading) return
    const userMsg: SessionMessage = { role: 'user', content: draft.trim() }
    const next = [...messages, userMsg]
    setMessages(next)
    setDraft('')
    setLoading(true)
    setError(null)

    const seriesCtx = buildArcSeriesContext(arcId as ArcId, sessionNum)
    const result = await callClaude({
      phase: 1,
      emotion: 'grief',
      intensity: 7,
      messages: next,
      firstName,
      seriesContext: seriesCtx,
    })
    setLoading(false)
    if ('error' in result) { setError(result.error); return }
    if (result.crisis) setCrisis(true)
    setMessages(prev => [...prev, { role: 'assistant', content: result.response }])
  }

  async function handleReadyToClose() {
    if (loading) return
    // Send a final message asking Claude for a somatic close
    const closeMsg: SessionMessage = {
      role: 'user',
      content: "I'm ready to close.",
    }
    const next = [...messages, closeMsg]
    setMessages(next)
    setLoading(true)
    setStage('closing')

    const seriesCtx = buildArcSeriesContext(arcId as ArcId, sessionNum)
    const result = await callClaude({
      phase: 1,
      emotion: 'grief',
      intensity: 7,
      messages: next,
      firstName,
      seriesContext: seriesCtx,
    })
    setLoading(false)
    if ('error' in result) { setError(result.error); return }
    setMessages(prev => [...prev, { role: 'assistant', content: result.response }])
  }

  // Build the "letter" text from all user turns
  function buildLetterText(): string {
    return messages
      .filter(m => m.role === 'user' && m.content !== "[I'm ready to close.]")
      .map(m => m.content)
      .join('\n\n')
  }

  function handleProceedToBurn() {
    if (!arc) return
    const session = arc.sessions[sessionNum - 1]
    if (session.alwaysSaved) {
      setStage('saved')
    } else if (!session.burnRequired && sessionNum === 3) {
      setStage('burn_choice')
    } else {
      setStage('burn')
    }
  }

  async function markSessionComplete(didSave: boolean, letterText?: string) {
    if (!arc) return
    setSavingCompletion(true)
    const now = new Date().toISOString()
    const sessionKey = `session_${sessionNum}_completed_at` as
      | 'session_1_completed_at'
      | 'session_2_completed_at'
      | 'session_3_completed_at'

    const isFinalSession = sessionNum === 3

    try {
      if (arcProgressId) {
        await Promise.resolve(
          db()
            .from('healing_arc_progress')
            .update({
              [sessionKey]: now,
              current_session: isFinalSession ? 3 : sessionNum + 1,
              ...(isFinalSession ? { status: 'completed', completed_at: now } : {}),
              updated_at: now,
            })
            .eq('id', arcProgressId)
        )
      }

      // Save the letter to arc_letters only when user chose to save
      if (didSave && letterText) {
        await Promise.resolve(
          db().from('arc_letters').insert({
            user_id: user.id,
            arc_progress_id: arcProgressId,
            arc_id: arc.id,
            session_number: sessionNum,
            letter_content: letterText,
          })
        )
      }
    } catch {
      // Non-fatal: progress update best-effort — user still sees completion
    }

    setSavingCompletion(false)
    setStage('complete')
  }

  // ── Invalid arc/session guard ──────────────────────────────────────────────
  if (!arc || !sessionMeta) {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: '#1C1128' }}>
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('arc.sessionNotFound')}</p>
          <button onClick={() => navigate('/healing-arcs')} style={{ color: '#9B72C0', fontSize: 14 }}>
            {t('arcs.backToArcs')}
          </button>
        </div>
      </div>
    )
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (stage === 'loading') {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: '#1C1128' }}>
        <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: arc.color, borderTopColor: 'transparent' }} />
      </div>
    )
  }

  // ── Consent ────────────────────────────────────────────────────────────────
  if (stage === 'consent') {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center px-6" style={{ background: '#1C1128' }}>
        <div className="max-w-sm w-full">
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: arc.color }}>
            {arc.name} · {sessionMeta.subtitle}
          </p>
          <p
            className="font-display text-2xl leading-snug mb-6"
            style={{ color: 'rgba(255,255,255,0.9)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {sessionMeta.title}
          </p>
          <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {sessionMeta.description}
          </p>
          <div
            className="rounded-xl px-4 py-4 mb-8"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {t('arc.consentBody')}
              <br /><br />
              {t('arc.consentPoint')}
            </p>
          </div>
          <button
            onClick={() => beginSession()}
            className="w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: arc.color, color: '#fff' }}
          >
            {t('arc.consentBegin')}
          </button>
          <button
            onClick={() => navigate(`/healing-arc/${arc.id}`)}
            className="w-full py-3 mt-2 text-sm transition-opacity hover:opacity-70"
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer' }}
          >
            {t('arc.notToday')}
          </button>
        </div>
      </div>
    )
  }

  // ── Burn ritual ────────────────────────────────────────────────────────────
  if (stage === 'burn') {
    const letterText = buildLetterText()
    return (
      <BurnRitual
        text={letterText || '[Letter content]'}
        onBurn={() => markSessionComplete(false)}
        onCancel={() => setStage('closing')}
        closingLine="Take a breath. Notice what's different in your body."
      />
    )
  }

  // ── Burn or save choice (S3 of Letters to Others) ─────────────────────────
  if (stage === 'burn_choice') {
    const letterText = buildLetterText()
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6"
        style={{ background: '#1C1128' }}
      >
        <div className="max-w-sm w-full text-center">
          <p
            className="font-display text-xl mb-3"
            style={{ color: 'rgba(255,255,255,0.9)', fontStyle: 'italic' }}
          >
            {t('arc.choiceHeading')}
          </p>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('arc.choiceBody')}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => markSessionComplete(true, letterText)}
              disabled={savingCompletion}
              className="w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
              style={{ background: arc.color, color: '#fff' }}
            >
              {savingCompletion ? t('arc.saving') : t('arc.saveToSanctuary')}
            </button>
            <button
              onClick={() => setStage('burn')}
              className="w-full py-4 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ background: '#FF4500', color: '#fff' }}
            >
              {t('arc.burnIt')}
            </button>
            <button
              onClick={() => setStage('closing')}
              className="w-full py-3 text-sm transition-opacity hover:opacity-70"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer' }}
            >
              {t('arc.goBack')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Always-saved (Letters to Self S3) ─────────────────────────────────────
  if (stage === 'saved') {
    const letterText = buildLetterText()
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6"
        style={{ background: '#1C1128' }}
      >
        <div className="max-w-sm w-full text-center">
          <p
            className="font-display text-xl mb-4"
            style={{ color: 'rgba(255,255,255,0.9)', fontStyle: 'italic' }}
          >
            {t('arc.vowHeading')}
          </p>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {t('arc.vowBody')}
          </p>
          <button
            onClick={() => markSessionComplete(true, letterText)}
            disabled={savingCompletion}
            className="w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: arc.color, color: '#fff' }}
          >
            {savingCompletion ? t('arc.saving') : t('arc.saveAndContinue')}
          </button>
        </div>
      </div>
    )
  }

  // ── Complete ───────────────────────────────────────────────────────────────
  if (stage === 'complete') {
    const isFinal = sessionNum === 3
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6 text-center"
        style={{ background: '#1C1128' }}
      >
        <div className="max-w-sm w-full">
          <p
            className="font-display text-2xl mb-4 leading-snug"
            style={{ color: 'rgba(255,255,255,0.85)', fontStyle: 'italic' }}
          >
            {isFinal ? arc.completionLine : sessionMeta.title}
          </p>
          {!isFinal && (
            <p className="text-sm leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('arc.sessionComplete', { n: sessionNum, total: arc.sessionCount })}
            </p>
          )}
          <p className="text-xs mb-10" style={{ color: 'rgba(255,255,255,0.25)' }}>
            {isFinal
              ? t('arc.takeYourTime')
              : t('arc.returnWhenReady', { pacing: arc.pacingSoftLabel, n: sessionNum + 1 })}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate(`/healing-arc/${arc.id}`)}
              className="w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
              style={{ background: arc.color, color: '#fff' }}
            >
              {t('arc.returnTo', { arc: arc.name })}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 text-sm transition-opacity hover:opacity-70"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
            >
              {t('common.home')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Writing / Closing ──────────────────────────────────────────────────────
  const isClosingStage = stage === 'closing'

  return (
    <div
      className="min-h-svh flex flex-col"
      style={{ background: '#1C1128' }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header
        className="flex items-center justify-between px-6 py-5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-widest mb-0.5" style={{ color: arc.color }}>
            {arc.name}
          </p>
          <p className="text-sm font-display italic" style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>
            {sessionMeta.title} · {sessionMeta.subtitle}
          </p>
        </div>
        {!isClosingStage && !loading && messages.length > 0 && (
          <button
            onClick={handleReadyToClose}
            className="text-xs px-3 py-1.5 rounded-lg transition-opacity hover:opacity-80"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
            }}
          >
            {t('arc.readyToClose')}
          </button>
        )}
      </header>

      {/* ── Crisis banner ─────────────────────────────────────────────────── */}
      {crisis && <CrisisBanner onDismiss={() => setCrisis(false)} />}

      {/* ── Message thread ────────────────────────────────────────────────── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-6"
        style={{ maxWidth: 640, margin: '0 auto', width: '100%' }}
      >
        {messages.map((m, i) => <MessageBubble key={i} msg={m} />)}
        {loading && <TypingIndicator />}

        {error && (
          <p className="text-xs text-center py-2" style={{ color: '#f87171' }}>
            {error} —{' '}
            <button
              onClick={() => sendMessage()}
              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', textDecoration: 'underline' }}
            >
              retry
            </button>
          </p>
        )}
      </div>

      {/* ── Input area or closing CTA ──────────────────────────────────────── */}
      {isClosingStage && !loading ? (
        // Closing: show burn/save CTA once Claude's close message is rendered
        <div
          className="px-6 pb-10 pt-4 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)', maxWidth: 640, margin: '0 auto', width: '100%' }}
        >
          <button
            onClick={handleProceedToBurn}
            className="w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: arc.color, color: '#fff' }}
          >
            {sessionMeta.burnRequired
              ? t('arc.proceedToBurn')
              : sessionMeta.alwaysSaved
                ? t('arc.saveThisVow')
                : t('arc.proceed')}
          </button>
        </div>
      ) : !isClosingStage ? (
        // Writing: textarea + send
        <div
          className="px-6 pb-8 pt-3 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)', maxWidth: 640, margin: '0 auto', width: '100%' }}
        >
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
          >
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  sendMessage()
                }
              }}
              placeholder={t('arc.writeHere')}
              rows={4}
              disabled={loading}
              className="w-full px-4 pt-4 pb-2 text-sm resize-none outline-none"
              style={{
                background: 'transparent',
                color: 'rgba(255,255,255,0.8)',
                fontFamily: 'inherit',
                lineHeight: 1.7,
              }}
            />
            <div className="flex items-center justify-between px-4 pb-3">
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
                {t('arc.shiftEnter')}
              </p>
              <button
                onClick={sendMessage}
                disabled={!draft.trim() || loading}
                className="px-4 py-1.5 rounded-lg text-xs font-medium transition-opacity"
                style={{
                  background: draft.trim() && !loading ? arc.color : 'rgba(255,255,255,0.08)',
                  color: draft.trim() && !loading ? '#fff' : 'rgba(255,255,255,0.25)',
                  border: 'none',
                  cursor: draft.trim() && !loading ? 'pointer' : 'not-allowed',
                }}
              >
                {t('common.send')}
              </button>
            </div>
          </div>
          {/* Mobile spacer */}
          <div className="h-2" />
        </div>
      ) : null}
    </div>
  )
}
